import { Loader2, Sparkles, Wand2, X, Sliders, Link2 } from 'lucide-react';
import { useGenerationStore } from '../../stores/generation-store';
import { useGenerate } from '../../hooks/use-generate';
import { useSettingsStore } from '../../stores/settings-store';
import { useAiAccessStore } from '../../stores/ai-access-store';
import { Button } from '../ui/Button';
import { useState } from 'react';
import { ReferenceDialog } from './ReferenceDialog';
import { SectionsBuilder } from './SectionsBuilder';
import { useEditorStore } from '../../stores/editor-store';

export function PromptInput() {
  const { prompt, isGenerating, generate, improve } = useGenerate();
  const setPrompt = useGenerationStore((s) => s.setPrompt);
  const reference = useGenerationStore((s) => s.reference);
  const action = useGenerationStore((s) => s.action);
  const error = useGenerationStore((s) => s.error);
  const warnings = useGenerationStore((s) => s.warnings);
  const clearError = useGenerationStore((s) => s.clearError);
  const clearWarnings = useGenerationStore((s) => s.clearWarnings);
  const cancelGeneration = useGenerationStore((s) => s.cancelGeneration);
  const setGenerationSettingsOpen = useSettingsStore((s) => s.setGenerationSettingsOpen);
  const setSettingsOpen = useSettingsStore((s) => s.setSettingsOpen);
  const hasKey = useAiAccessStore((s) => s.hasKey);
  const provider = useAiAccessStore((s) => s.provider);
  const openrouterModel = useAiAccessStore((s) => s.openrouterModel);
  const tokenInput = useAiAccessStore((s) => s.tokenInput);
  const tokenOutput = useAiAccessStore((s) => s.tokenOutput);
  const tokenTotal = useAiAccessStore((s) => s.tokenTotal);
  const aiLoading = useAiAccessStore((s) => s.loading);
  const usedToday = useAiAccessStore((s) => s.usedToday);
  const dailyLimit = useAiAccessStore((s) => s.dailyLimit);
  const remainingToday = useAiAccessStore((s) => s.remainingToday);
  const [refOpen, setRefOpen] = useState(false);
  const editor = useEditorStore((s) => s.editorInstance);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      generate();
    }
  };

  return (
    <div className="flex flex-col gap-0 border-t border-surface-lighter bg-surface-light">
      <SectionsBuilder />
      <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center justify-between text-[11px]">
        {aiLoading ? (
          <span className="text-text-muted">Checking AI access...</span>
        ) : hasKey && provider === 'gemini' ? (
          <>
            <span className="text-text-muted">
              Daily usage: {usedToday}/{dailyLimit}
              {' \u00B7 '}
              gemini
            </span>
            <span className="text-text-muted">Remaining: {remainingToday}</span>
          </>
        ) : hasKey && provider === 'openrouter' ? (
          <>
            <span className="text-text-muted">
              openrouter:{openrouterModel.split('/').pop()?.split('-')[0] ?? openrouterModel}
              {' \u00B7 '}
              tokens in/out/total: {tokenInput}/{tokenOutput}/{tokenTotal}
            </span>
            <span className="text-text-muted">Unlimited</span>
          </>
        ) : (
          <>
            <span className="text-amber-300">{provider === 'openrouter' ? 'OpenRouter key is not connected.' : 'Gemini key is not connected.'}</span>
            <button
              onClick={() => setSettingsOpen(true)}
              className="text-accent hover:underline"
            >
              Open Settings
            </button>
          </>
        )}
      </div>
      {error && (
        <div className="flex items-center justify-between px-3 py-2 rounded bg-red-900/30 text-red-300 text-xs">
          <span>{error}</span>
          <button onClick={clearError} className="ml-2 hover:text-red-200">
            &times;
          </button>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="flex items-start justify-between gap-3 px-3 py-2 rounded bg-amber-900/30 text-amber-200 text-xs">
          <div className="min-w-0">
            <div className="font-semibold">QC warnings</div>
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            {warnings.slice(0, 5).map((w) => (
              <li key={w}>{w}</li>
            ))}
            {warnings.length > 5 && <li>…and {warnings.length - 5} more</li>}
          </ul>
          </div>
          <button onClick={clearWarnings} className="shrink-0 hover:text-amber-100" title="Hide warnings">
            &times;
          </button>
        </div>
      )}
      <div className="flex gap-2 items-end">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe the UI you want to generate..."
          rows={2}
          disabled={isGenerating}
          className="flex-1 px-3 py-2 bg-surface border border-surface-lighter rounded text-sm text-text placeholder:text-text-muted resize-none focus:outline-none focus:border-accent disabled:opacity-50"
        />

        <Button
          variant={reference ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setRefOpen(true)}
          title="Reference"
          disabled={isGenerating}
          className="h-9 w-9 px-0"
        >
          <Link2 size={16} />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setGenerationSettingsOpen(true)}
          title="Generation Settings"
          disabled={isGenerating}
          className="h-9 w-9 px-0"
        >
          <Sliders size={16} />
        </Button>

        {isGenerating ? (
          <Button onClick={cancelGeneration} variant="secondary" className="inline-flex items-center justify-center">
            <X size={16} />
            <span className="ml-1.5">Cancel</span>
          </Button>
        ) : (
          <>
            <Button
              onClick={improve}
              variant="secondary"
              disabled={!editor}
              className="inline-flex items-center justify-center"
              title={!editor ? 'Open a project first' : 'Refine current page'}
            >
              <Wand2 size={16} />
              <span className="ml-1.5">Improve (Local)</span>
            </Button>
            <Button onClick={generate} disabled={!prompt.trim()} className="inline-flex items-center justify-center">
              <Sparkles size={16} />
              <span className="ml-1.5">Generate</span>
            </Button>
          </>
        )}
        {isGenerating && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted">
            <Loader2 size={16} className="animate-spin" />
            <span>{action === 'improve' ? 'Improving...' : 'Generating...'}</span>
          </div>
        )}
      </div>
      <ReferenceDialog open={refOpen} onClose={() => setRefOpen(false)} />
      </div>
    </div>
  );
}
